import './scss/style.scss';
import 'core-js/stable';
import 'fastestsmallesttextencoderdecoder';
import 'webcrypto-shim';
import 'regenerator-runtime/runtime';
import 'classlist-polyfill';
import { ThemeProvider } from '@material-ui/core/styles';
import { hydrateRoot, createRoot, Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import * as React from 'react';
import initData from './utilities/init-data';
import gtmTracker from './utilities/gtm-tracker';
import * as HeapUtils from './utilities/heap';
import { theme } from './theme/theme';
import Router from './routes/router';
import Store from './redux/store';
import NotificationContainer from './components/notification-container';
import ErrorBoundary from './components/_common/error-boundary';
import { fetchUserProfile, fetchUserPreference } from './redux/user/user.actions';
import security from './utilities/security';
import marketoTracker from './utilities/marketo-tracker';
import cookieNotice from './utilities/cookie-notice';
import setLanguageContext from './utilities/set-language-context';
import spgVariables from './utilities/spg-variables';
import featureFlags from './utilities/feature-flags';
import serviceWorkerRegistration from './serviceWorkerRegistration';
import history from './utilities/history';
import { clearAllProfiles, fetchOpenSearchDataset } from './redux/datasets/datasets.actions';
import { get, isAdminUser } from './helpers';
import detectIncognitoMode from './utilities/detectIncognito';
import { fetchCollections } from './redux/collections/collections.actions';
import { fetchQueryBuilderSteps } from './redux/querybuilder/query-builder.actions';
import { fetchAnnouncementsSummary, fetchAnnouncementsArchive } from './redux/announcements/announcements.actions';

// Extend the Window interface to include RequestFileSystem, webkitRequestFileSystem, OnetrustActiveGroups, and openDatabase
declare global {
    interface Window {
        RequestFileSystem?: any;
        webkitRequestFileSystem?: any;
        OnetrustActiveGroups?: string;
        openDatabase?: (...args: any[]) => any;
    }
}

const spgVars = spgVariables();
const anonymousText = 'Anonymous';
const Window = window as AnonymousObject;

const domNode = document.getElementById('root');

if (!domNode) {
    throw new Error("The element #root wasn't found");
}
const rootElement = domNode;

const appElement = (
    <Provider store={Store}>
        <ThemeProvider theme={theme}>
            <NotificationContainer />
            <ErrorBoundary isSilent>
                <Router />
            </ErrorBoundary>
        </ThemeProvider>
    </Provider>
);

Window.dataLayer = Window.dataLayer || [];
function renderApp() {
    if (spgVars.SSR) {
        hydrateRoot(rootElement, appElement);
        return;
    }

    const clientRoot: Root = createRoot(rootElement);
    clientRoot.render(appElement);
}

setLanguageContext();
cookieNotice();
initData(window.location.pathname);
// gtmTracker();

async function maybeInitHeap() {
    // Check OneTrust cookie consent
    const oneTrustCookies = window.OnetrustActiveGroups || '';
    const perfCookiesEnabled = oneTrustCookies.includes('C0002');
    const groups = oneTrustCookies.split(',').filter(Boolean);
    const allRejected = groups.length === 1 && groups[0] === 'C0001';

    // Check incognito
    const incognito = await detectIncognitoMode();

    if (!allRejected && perfCookiesEnabled) {
        gtmTracker();
        // Your environment logic here
        if (window.location.hostname.includes('dev')) {
            HeapUtils.default.initializeHeap('2189945135');
        } else if (window.location.hostname.includes('stage')) {
            HeapUtils.default.initializeHeap('3153181355');
        } else {
            HeapUtils.default.initializeHeap('768679386');
        }
    } else {
        // Disable Heap tracking explicitly
        if (window.heap && window.heap.loaded) {
            // Optionally, you can also remove the Heap script tag if you want
            const heapScript = document.querySelector('script[src*="heap_config.js"]');
            if (heapScript && heapScript.parentNode) {
                heapScript.parentNode.removeChild(heapScript);
            }
            window.heap = undefined;
            window.location.reload();
        }
        console.log('Heap tracking disabled due to cookie or privacy settings.');
    }
}

// If already set, run immediately; otherwise, wait for OneTrust
if (typeof window.OnetrustActiveGroups === 'string' && window.OnetrustActiveGroups.length > 0) {
    maybeInitHeap();
}

function hasFullConsent() {
    const oneTrustCookies = window.OnetrustActiveGroups || '';
    const requiredCategories = ['C0001', 'C0002', 'C0003', 'C0004'];
    return requiredCategories.every(cat => oneTrustCookies.includes(cat));
}

function onOneTrustReady() {
    maybeInitHeap();
    // window.removeEventListener('OneTrustGroupsUpdated', onOneTrustReady);
    // If user is authenticated and has full consent, add user data to Heap
    // Avoid calling authenticate() again here to prevent a duplicate OAuth token request.
    // The main authenticate() call below already handles Heap user data when hasFullConsent() is true.
    // Here we only need to handle the case where OneTrust fires *after* auth has already completed.
    if (hasFullConsent()) {
        const userState = Store.getState().user;
        const user = userState && userState.userInfo;
        if (user && user.KeyOnlineUser && user.SubscriberAccount) {
            HeapUtils.default.addUserData(user.KeyOnlineUser, user.SubscriberAccount);
        }
    }
}

// Always listen for consent changes
window.addEventListener('OneTrustGroupsUpdated', onOneTrustReady);

security
    .authenticate()
    .then(token => {
        if (token) {
            Store.dispatch(
                fetchUserProfile.action({
                    onSuccess: (user: UserInfo) => {
                        featureFlags.init(spgVars.LD_CLIENT_ID, spgVars.LD_SECRET_HASH, spgVars.LD_RELAYPROXY, user);
                        console.log('Key Online User : ', user.KeyOnlineUser);
                        const areCollectionsEnabled = get(Store.getState(), 'shared.flags.fflagMpIsCollectionsEnabled', false);
                        Store.dispatch(fetchCollections());
                        Store.dispatch(fetchAnnouncementsSummary());
                        Store.dispatch(fetchAnnouncementsArchive());
                        Window.dataLayer.push({
                            event: 'login',
                            keyOnlineUser: user.KeyOnlineUser
                        });
                        const noCache = isAdminUser(user && user.Email);
                        if (noCache) {
                            console.log('Admin user detected, clearing all datasets cache');
                            Store.dispatch(clearAllProfiles());
                        }
                        // Force a refetch of the open search results now that we're authenticated: a cached
                        // anonymous response (e.g. restored from localStorage) would otherwise be reused as-is
                        // and hide internal-only tiles that only logged-in users should see.
                        Store.dispatch(fetchOpenSearchDataset(true));
                        if (hasFullConsent()) {
                            HeapUtils.default.addUserData(user.KeyOnlineUser, user.SubscriberAccount);
                        }
                    }
                })
            );
            // Code snippet to have backward and forward compatibility from OKTA to IDM or viceversa hidden iframe
            const kenshoAutoLoginURL = 'https://secure.signin.spglobal.com/app/spglobaliam_wam12cprod_1/exk1ll4itvcxcfGlc1d8/sso/saml?RelayState=https://login.spglobal.com/favicon.ico';
            const noscript = document.createElement('noscript');
            const oktaiframe = document.createElement('iframe');
            oktaiframe.id = 'OktaToIDM12cBackwardCHandShakeUrlIFrame';
            oktaiframe.src = kenshoAutoLoginURL;
            oktaiframe.height = '0';
            oktaiframe.width = '0';
            oktaiframe.style.visibility = 'hidden';
            oktaiframe.style.display = 'none';
            noscript.appendChild(oktaiframe);
            document.getElementsByTagName('body')[0].appendChild(noscript);
            Store.dispatch(fetchUserPreference.action(null));
            // Fetch query builder steps only if user is on /extract page
            if (window.location.pathname.toLowerCase().includes('/extract')) {
                Store.dispatch(fetchQueryBuilderSteps());
            }
        } else {
            featureFlags.init(spgVars.LD_CLIENT_ID, spgVars.LD_SECRET_HASH, spgVars.LD_RELAYPROXY);
            Window.dataLayer.push({
                KeyOnlineUser: anonymousText,
                SubscriberAccount: anonymousText,
                CompanyType: anonymousText,
                PrimaryJobFunction: anonymousText,
                UserWebpartProfile: anonymousText
            });
        }
    })
    .finally(() => {
        renderApp();
        marketoTracker();
    });

serviceWorkerRegistration();
